import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, KeyRound, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Logo, LangToggle, ThemeToggle } from "@/components/atlas/Shared";
import { Field, GoldButton } from "@/components/atlas/Fields";
import { api } from "@/lib/api";
import { useAuth, homeFor } from "@/lib/auth";
import { useLang, errorText } from "@/lib/i18n";


const StaffLogin = ({ onDone }) => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email");
  const [demo, setDemo] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/request", { email });
      setDemo(r.data.demo_code || "");
      setStep("code");
    } catch (err) {
      toast.error(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/verify", { email, code });
      onDone(r.data);
    } catch (err) {
      toast.error(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };

  if (step === "email") {
    return (
      <form onSubmit={request} className="space-y-5" data-testid="otp-request-form">
        <Field label={t("login.email")} hint={t("login.staffHint")}>
          <Input data-testid="otp-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-none bg-white" placeholder="prenom.nom@courriel.com" />
        </Field>
        <GoldButton data-testid="otp-request-btn" disabled={busy} className="w-full h-12">
          <Mail className="w-4 h-4" /> {t("login.sendCode")}
        </GoldButton>
      </form>
    );
  }
  return (
    <form onSubmit={verify} className="space-y-5" data-testid="otp-verify-form">
      <p className="text-sm text-neutral-600">{t("login.codeSent")} <strong>{email}</strong></p>
      {demo && (
        <div className="rounded-xl border border-[#C5A059]/40 bg-[#C5A059]/[0.08] px-4 py-3 text-sm" data-testid="otp-demo-code">
          {t("login.demoCode")} <span className="mono font-semibold tracking-[0.3em]">{demo}</span>
        </div>
      )}
      <Input data-testid="otp-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-14 rounded-none bg-white text-center mono text-2xl tracking-[0.5em]" placeholder="••••••" />
      <GoldButton data-testid="otp-verify-btn" disabled={busy || code.length !== 6} className="w-full h-12">
        {t("login.verify")} <ArrowRight className="w-4 h-4" />
      </GoldButton>
      <button type="button" data-testid="otp-change-email" onClick={() => { setStep("email"); setCode(""); }} className="text-xs text-neutral-500 hover:text-black underline underline-offset-4">
        {t("login.otherEmail")}
      </button>
    </form>
  );
};

const MgmtLogin = ({ onDone }) => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [byCode, setByCode] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      onDone(r.data);
    } catch (err) {
      toast.error(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };
  const switcher = (
    <button type="button" data-testid="mgmt-login-switch" onClick={() => setByCode(!byCode)} className="text-xs text-neutral-500 hover:text-black underline underline-offset-4">
      {t(byCode ? "login.usePassword" : "login.useCode")}
    </button>
  );
  if (byCode) return <div className="space-y-5"><StaffLogin onDone={onDone} />{switcher}</div>;
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="password-login-form">
      <Field label={t("login.email")}>
        <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-none bg-white" />
      </Field>
      <Field label={t("login.password")}>
        <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-none bg-white" />
      </Field>
      <GoldButton data-testid="login-submit-btn" disabled={busy} className="w-full h-12">
        <KeyRound className="w-4 h-4" /> {t("login.signIn")}
      </GoldButton>
      {switcher}
    </form>
  );
};

export default function Login() {
  const { t } = useLang();
  const { user, ready, signIn } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("staff");
  if (ready && user) return <Navigate to={homeFor(user.role)} replace />;
  const done = (data) => {
    toast.dismiss();
    signIn(data);
    navigate(homeFor(data.user.role));
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="dark-zone hidden lg:flex flex-col bg-[#02231C] overflow-hidden p-12 text-white" data-testid="login-brand-panel">
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <img src="/atlas-logo.webp" alt="Atlas Event Services" data-testid="login-brand-logo" className="w-full max-w-[400px] max-h-[60vh] object-contain rise" />
        </div>
        <div className="shrink-0">
          <div>
            <div className="w-16 h-px bg-[#C5A059] mb-6" />
            <p className="mt-6 text-xs tracking-[0.3em] uppercase text-white/50">Montréal · Québec</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col px-6 sm:px-12 py-8">
        <div className="flex items-center justify-between">
          <div className="lg:hidden"><Logo /></div>
          <div className="ml-auto flex items-center gap-2"><LangToggle /><ThemeToggle /></div>
        </div>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm mx-auto rise">
            <div className="eyebrow mb-3">Atlas Event Services</div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">{t("login.title")}</h1>
            <p className="text-sm text-neutral-500 mt-3 mb-8">{t("login.sub")}</p>
            <div className="grid grid-cols-2 rounded-full border border-white/10 p-1 mb-8" data-testid="login-mode-tabs">
              {["staff", "mgmt"].map((m) => (
                <button key={m} data-testid={`login-tab-${m}`} onClick={() => setMode(m)}
                  className={`press rounded-full py-2.5 text-sm font-medium ${mode === m ? "bg-[#C5A059] text-[#0A0A0C] font-semibold" : "text-neutral-500 hover:text-black"}`}>
                  {t(`login.${m}`)}
                </button>
              ))}
            </div>
            {mode === "staff" ? <StaffLogin onDone={done} /> : <MgmtLogin onDone={done} />}
          </div>
        </div>
      </div>
    </div>
  );
}
